// @flow
import React, { Component } from "react";
import type { ComponentType } from "react";
import { connect } from "react-redux";
import cx from "classnames";
import glamorous from "glamorous";
import { createStructuredSelector } from "reselect";
import styled, { ThemeProvider } from "styled-components";
import { WIDGET_VIEW_TYPES, INLINE_HEIGHT } from "@cld/upload-widget";
import appConfig from "appConfig";
import getMediaQuery from "utils/sassVarsUtils";
import { convertPaletteToCldTheme } from "utils/cldThemeAdapter";
import consts from "common/consts";
import CloseButton from "root/components/CloseButton/CloseButton";
import MobileLandscapeOverlay from "root/components/MobileLandscapeOverlay/MobileLandscapeOverlay";
import bindActions, { types as ats } from "../../actions";
import {
	selectPalette,
	selectIsResponsiveWidth,
	selectWidgetViewType,
	selectIsInCrop,
	selectIsMultipleEnabled,
	selectCloudName,
	selectConfigLanguage,
	selectDictionaryTextVersion,
	selectShouldShowAdvancedOptions,
	selectIsInlineMode,
	selectFrameBackground,
	selectIsInQueue,
	selectTabInsideWidget
} from "../../selectors";
import TopMenu from "../TopMenu";
import Notification from "../Notification/Notification";
import ResponsiveWidthMonitor from "../ResponsiveWidthMonitor";
import Progress from "../Progress/Progress";
import Styles from "../Styles/Styles";

import "./App.scss";
import { isVisibleAndTabbable } from "~/src/utils/domUtils";

type Props = {
	children: any[],
	isMultiple: Boolean,
	isInline: Boolean,
	cloudName: ?string,
	language: string,
	showAdvancedOptions: boolean,
	frameBackground: Object,
	palette: Object,
	isResponsiveWidth: boolean,
	widgetViewType: number,
	isInCrop: boolean,
	isInQueue: boolean,
	tabInsideWidget: boolean;
	[string]: Function,
};

const GWidgetWrap = glamorous.div((props: Props) => cx("popup-box overflow-hidden br0 h-100 w-100 top-0 left-0",
	{ "bg-source-bg": !props.cloudName }), {
	transform: "none",
	margin: "0 auto",
	boxShadow: "0 20px 30px rgba(0,0,0,.7)",
}, (props: Props) => ({
	[getMediaQuery("ns")]: {
		position: props.isInline ? "static" : "absolute",
		top: props.isInline ? null : "50%",
		left: props.isInline ? null : "50%",
		transform: props.isInline ? null : "translate(-50%,-50%)",
		borderRadius: props.isInline ? null : 5,
		width: props.isInline ? null : 767,
		height: INLINE_HEIGHT,
	},
}));

const GOverlay = glamorous.div(({ isMultiple, isQueue, showAdvancedOptions }) => cx("fixed top-0 bottom-0 left-0 right-0 widget upload-popup font-root", {
	multiple_enabled: isMultiple,
	"queue-enabled": isQueue,
	"advanced-options": showAdvancedOptions,
}),
({ cloudName, background }) => ({
	zIndex: 6,
	backgroundColor: cloudName ? background : "none",
	// using media query to avoid using selector to get widget's view type (minified or not)
	// because then the App re-renders for every change from mini to expanded when in queue
	"@media(max-height: 100px)": {
		background: "none",
	},
}));

export const StyledCloseButton: ComponentType<{ isResponsiveWidth: boolean }> = styled(CloseButton)`
    display: ${({ isResponsiveWidth }) => (isResponsiveWidth ? "inline-flex" : "block")};
    position: absolute;
    height: ${({ isResponsiveWidth }) => (isResponsiveWidth ? `${consts.TAB_HEADER_HEIGHT}px` : "auto")};
    right: ${({ isResponsiveWidth }) => (isResponsiveWidth ? 12 : 16)}px;
    top: ${({ isResponsiveWidth }) => (isResponsiveWidth ? 0 : 18)}px;
    z-index: 10;
    cursor: pointer;
`;

export class App extends Component<Props> {
	lastPathName;

	onKeyDown = (event) => {
		if(this.props.isTabInsideWidget && event.key === "Tab") {
			const tabindexElements = [...document.querySelectorAll("[tabindex]")].filter(isVisibleAndTabbable);
			const index = tabindexElements.indexOf(event.target);

			// on the last element that was tabbed focus on the first element
			if (index === tabindexElements.length - 1 && !event.shiftKey) {
				event.preventDefault();
				tabindexElements[0].focus();
			}

			// if you tab back using shift and get to first element, focus on the last element
			if (index === 0 && event.shiftKey) {
				event.preventDefault();
				tabindexElements[tabindexElements.length - 1].focus();
			}
		}
		else if (event.key === "Enter" && document.activeElement === event.target) {
			event.preventDefault();
			const { previousSibling } = event.target;
			const target = previousSibling?.nodeName === "INPUT" ? previousSibling : event.target;
			target.click();
		}
	}

	componentDidMount() {
		this.props[ats.DO_REGISTER_GLOBAL_KEY_EVENTS]();
	}

	componentDidUpdate(prevProps: Props) {
		if (!prevProps.cloudName && this.props.cloudName) {
			this.props[ats.DO_REGISTER_GLOBAL_KEY_EVENTS]();
		}
	}

	renderCloseButton = () => !this.props.isInline && !this.props.isInCrop &&
		this.props.widgetViewType !== WIDGET_VIEW_TYPES.MINI &&
		<StyledCloseButton isResponsiveWidth={this.props.isResponsiveWidth}
						   data-test="close-dialog-button"/>;

	// avoid rendering app tabs before widget config is set into the store
	renderAppContent = (isQueue, isCrop) => (this.props.cloudName ?
			<>
				{!isQueue && !isCrop && <TopMenu key="app_top_menu"/>}
				{this.props.children}
			</> :
			<Progress size={42} overlay key="app_pi"/>
	);

	render() {
		return <ThemeProvider theme={convertPaletteToCldTheme(this.props.palette)}>
			<ResponsiveWidthMonitor query={`(max-width: ${appConfig.ui.responsiveWidth})`}>
				<Styles key="widget_styles"/>
				<GOverlay data-test="upload-widget-popup" data-language={this.props.language}
						  cloudName={this.props.cloudName}
						  background={this.props.frameBackground}
						  isMultiple={this.props.isMultiple}
						  isQueue={this.props.isInQueue}
						  onKeyDown={this.onKeyDown}
						  showAdvancedOptions={this.props.showAdvancedOptions}>
					<GWidgetWrap {...this.props}>
						<Notification/>
						{this.renderCloseButton()}
						{this.renderAppContent(this.props.isInQueue, this.props.isInCrop)}
						<MobileLandscapeOverlay/>
					</GWidgetWrap>
				</GOverlay>
			</ResponsiveWidthMonitor>
		</ThemeProvider>;
	}
}

export default connect(
	createStructuredSelector({
		isMultiple: selectIsMultipleEnabled,
		isInline: selectIsInlineMode,
		cloudName: selectCloudName,
		language: selectConfigLanguage,
		textVersion: selectDictionaryTextVersion,
		showAdvancedOptions: selectShouldShowAdvancedOptions,
		frameBackground: selectFrameBackground,
		palette: selectPalette,
		isResponsiveWidth: selectIsResponsiveWidth,
		widgetViewType: selectWidgetViewType,
		isInCrop: selectIsInCrop,
		isInQueue: selectIsInQueue,
		isTabInsideWidget: selectTabInsideWidget
	}),
	bindActions,
)(App);
